import { db } from '../../core/database/firebase.js'
import type { Notification } from './notifications.schemas.js'
import { logger } from '../../utils/logger.js'
import { removeUndefinedFields } from '../../utils/sanitize.js'
import { createBatchBuilder } from '../../common/utils/firestore-batch.js'
import type { FirestoreDoc } from '../../common/types/firestore.js'

const COLLECTION = 'notifications'

export class NotificationsRepository {
  /**
   * Create new notification
   */
  async create(notification: Omit<Notification, 'id'>): Promise<Notification> {
    try {
      // Remove undefined fields (Notification has optional: data?)
      const sanitizedData = removeUndefinedFields({
        ...notification,
        createdAt: Date.now(),
        read: false
      })
      const docRef = await db.collection(COLLECTION).add(sanitizedData)

      const doc = await docRef.get()
      return {
        id: doc.id,
        ...doc.data()
      } as Notification
    } catch (error) {
      logger.error('Failed to create notification', { error, notification })
      throw error
    }
  }

  /**
   * Get user notifications
   */
  async getByUserId(
    userId: string,
    options: {
      limit?: number;
      offset?: number;
      unreadOnly?: boolean;
    } = {}
  ): Promise<Notification[]> {
    try {
      const { limit = 20, unreadOnly = false } = options

      let query = db
        .collection(COLLECTION)
        .where('userId', '==', userId)
        .orderBy('createdAt', 'desc')

      if (unreadOnly) {
        query = query.where('read', '==', false)
      }

      // Use cursor-based pagination (Rule F4) - offset parameter deprecated
      const snapshot = await query.limit(limit).get()

      return snapshot.docs.map((doc: FirestoreDoc) => ({
        id: doc.id,
        ...doc.data()
      })) as Notification[]
    } catch (error) {
      logger.error('Failed to get notifications', { error, userId, options })
      throw error
    }
  }

  /**
   * Count unread notifications
   */
  async countUnread(userId: string): Promise<number> {
    try {
      const snapshot = await db
        .collection(COLLECTION)
        .where('userId', '==', userId)
        .where('read', '==', false)
        .count()
        .get()

      return snapshot.data().count
    } catch (error) {
      logger.error('Failed to count unread notifications', { error, userId })
      throw error
    }
  }

  /**
   * Mark notifications as read
   */
  async markAsRead(notificationIds: string[]): Promise<void> {
    try {
      const batch = createBatchBuilder(db)

      for (const id of notificationIds) {
        batch.addUpdate(COLLECTION, id, { read: true })
      }

      await batch.commit()
    } catch (error) {
      logger.error('Failed to mark notifications as read', { error, notificationIds })
      throw error
    }
  }

  /**
   * Mark all notifications as read for user
   */
  async markAllAsRead(userId: string, before?: number): Promise<number> {
    try {
      let query = db
        .collection(COLLECTION)
        .where('userId', '==', userId)
        .where('read', '==', false)

      if (before) {
        query = query.where('createdAt', '<', before)
      }

      const snapshot = await query.get()
      const batch = createBatchBuilder(db)

      for (const doc of snapshot.docs as FirestoreDoc[]) {
        batch.updateRef(doc.ref, { read: true })
      }

      await batch.commit()
      return snapshot.size
    } catch (error) {
      logger.error('Failed to mark all as read', { error, userId, before })
      throw error
    }
  }

  /**
   * Delete old notifications (cleanup job)
   * Uses SmartBatch to automatically handle Firestore's 500 operation limit.
   */
  async deleteOlderThan(days: number): Promise<number> {
    try {
      const cutoff = Date.now() - days * 24 * 60 * 60 * 1000
      let totalDeleted = 0
      let hasMore = true

      // Loop until no more documents to delete
      while (hasMore) {
        const snapshot = await db
          .collection(COLLECTION)
          .where('createdAt', '<', cutoff)
          .limit(500)
          .get()

        if (snapshot.empty) {
          break
        }

        const batch = createBatchBuilder(db)
        for (const doc of snapshot.docs as FirestoreDoc[]) {
          batch.deleteRef(doc.ref)
        }
        await batch.commit()

        totalDeleted += snapshot.size
        logger.debug('Deleted batch of old notifications', { batchSize: snapshot.size, totalDeleted, days })

        // If we got less than the limit, we're done
        if (snapshot.size < 500) {
          hasMore = false
        }
      }

      logger.info('Completed deleting old notifications', { totalDeleted, days })
      return totalDeleted
    } catch (error) {
      logger.error('Failed to delete old notifications', { error, days })
      throw error
    }
  }
}
